import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuLabel,
 DropdownMenuSeparator,
 DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
 Sidebar,
 SidebarContent,
 SidebarFooter,
 SidebarGroup,
 SidebarGroupLabel,
 SidebarHeader,
 SidebarInset,
 SidebarMenu,
 SidebarMenuButton,
 SidebarMenuItem,
 SidebarMenuSub,
 SidebarMenuSubButton,
 SidebarMenuSubItem,
 SidebarProvider,
 SidebarTrigger,
 useSidebar,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { sectorToneClasses } from "@shared/sectors";
import {
 BarChart3,
 BookOpen,
 Briefcase,
 Building2,
 CalendarClock,
 ChevronDown,
 Files,
 LayoutDashboard,
 LogOut,
 MessageSquare,
 PanelLeft,
 Receipt,
 Settings,
 ShieldCheck,
 Upload,
 Users,
 Activity,
 FolderOpen,
 Bell,
 UserCircle,
 Mail,
 AlertTriangle,
 Palette,
 Crown,
 Sun,
 Moon,
 ShieldAlert,
 Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useFocusMasterTokenLock } from "@/hooks/useFocusMasterTokenLock";
import { SoFaciliteiLogo } from "./SoFaciliteiLogo";
import { AdminThemeModal } from "./AdminThemeModal";
import { Button } from "./ui/button";
import { useTheme } from "@/contexts/ThemeContext";

const SIDEBAR_WIDTH_KEY = "so-facilitei-sidebar-width";
const DEFAULT_WIDTH = 250;

type LinkEntry = {
 type: "link";
 label: string;
 path: string;
 icon: React.ComponentType<{ className?: string }>;
};

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
 const [sidebarWidth] = useState<number>(() => {
 if (typeof window === "undefined") return DEFAULT_WIDTH;
 const saved = window.localStorage.getItem(SIDEBAR_WIDTH_KEY);
 return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
 });

 const { loading, user } = useAuth();

 if (loading) {
 return (
 <div className="min-h-screen flex items-center justify-center brand-bg-gradient">
 <div className="flex flex-col items-center gap-4 text-muted-foreground">
 <SoFaciliteiLogo variant="horizontal" height={48} />
 <span className="text-sm">Carregando sua área do cliente...</span>
 </div>
 </div>
 );
 }

 if (!user) {
 return (
 <div className="min-h-screen flex items-center justify-center brand-bg-gradient">
 <div className="flex flex-col items-center gap-6 p-8 max-w-md w-full bg-card brand-card-shadow rounded-2xl">
 <SoFaciliteiLogo variant="horizontal" height={48} />
 <div className="flex flex-col items-center gap-2 text-center">
 <h1 className="text-xl font-display font-bold tracking-tight">Acesso restrito</h1>
 <p className="text-sm text-muted-foreground">
 Entre com sua conta para acessar a Área do Cliente.
 </p>
 </div>
 <Button
 onClick={() => {
 window.location.href = "/login";
 }}
 size="lg"
 className="w-full bg-[var(--brand-orange)] text-[var(--brand-orange-foreground)] hover:opacity-90"
 >
 ÁREA DO CLIENTE
 </Button>
 </div>
 </div>
 );
 }

 return (
 <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
 <DashboardLayoutContent>{children}</DashboardLayoutContent>
 </SidebarProvider>
 );
}

function DashboardLayoutContent({ children }: { children: React.ReactNode }) {
  const { user, logout, isImpersonating, refresh } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const isMobile = useIsMobile();
  const isSystemAdmin = user?.role === "admin" || user?.role === "admin-interno";
  const isManager = user?.role === "manager";
  const isAdmin = isSystemAdmin || isManager;

  const unimpersonateMut = (trpc as any).auth.unimpersonate.useMutation({
    onSuccess: async () => {
      toast.success("Retornou ao Administrador com sucesso!");
      await refresh();
      window.location.reload();
    },
    onError: (err: any) => {
      toast.error("Erro ao retornar: " + (err?.message || "Erro"));
    },
  });

  const myCompanyQuery = trpc.companies.myCompany.useQuery(undefined, {
    enabled: !isAdmin,
  });
  const { theme, setTheme, toggleTheme, setCompanyBranding } = useTheme();

  const userPrimary = (user as any)?.primaryColor;
  const userSecondary = (user as any)?.secondaryColor;
  const userThemePref = (user as any)?.themePreference;
  const companyData = myCompanyQuery.data;

  // Sincroniza o tema individual do usuário ativo (carregado do banco ou do cache exclusivo deste usuário)
  useEffect(() => {
    if (user?.id) {
      if (userThemePref === "dark" || userThemePref === "light") {
        setTheme(userThemePref, user.id);
      } else {
        const host = typeof window !== "undefined" ? window.location.hostname.replace(/:\d+$/, "") : "";
        const saved = typeof window !== "undefined" ? localStorage.getItem(`theme_${host}_user_${user.id}`) : null;
        if (saved === "dark" || saved === "light") {
          setTheme(saved as "dark" | "light", user.id);
        } else {
          setTheme("light", user.id);
        }
      }
    }
  }, [user?.id, userThemePref, setTheme]);

  useEffect(() => {
    if (userPrimary) {
      setCompanyBranding({
        primaryColor: userPrimary,
        secondaryColor: userSecondary,
        themePreference: userThemePref,
      });
    } else if (companyData) {
      setCompanyBranding(companyData as any);
    }
  }, [userPrimary, userSecondary, userThemePref, companyData, setCompanyBranding]);

  // Ativa a classe .dark enquanto estiver no Painel e remove ao sair para o site institucional
  useEffect(() => {
    if (typeof document !== "undefined") {
      if (theme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
    return () => {
      if (typeof document !== "undefined") {
        document.documentElement.classList.remove("dark");
      }
    };
  }, [theme]);

 const [searchParamsStr, setSearchParamsStr] = useState(() =>
 typeof window !== "undefined" ? window.location.search : ""
 );

 useEffect(() => {
 const handlePopçõesState = () => {
 setSearchParamsStr(window.location.search);
 };
 window.addEventListener("popstate", handlePopçõesState);
 return () => window.removeEventListener("popstate", handlePopçõesState);
 }, []);

 const sectorsQuery = trpc.sectors.list.useQuery();
 const sectorList = useMemo(() => sectorsQuery.data || [], [sectorsQuery.data]);

 const [openThemeModal, setOpenThemeModal] = useState(false);
 const [openSetores, setOpenSetores] = useState(true);
 const [openNotas, setOpenNotas] = useState(true);
 const [openAdmin, setOpenAdmin] = useState(true);
 const [openFiscal, setOpenFiscal] = useState(true);

 const overviewLinks: LinkEntry[] = useMemo(
 () => [
 {
 type: "link",
 label: isAdmin ? "Painel administrativo" : "Início",
 path: isAdmin ? "/admin" : "/portal",
 icon: LayoutDashboard,
 },
 {
 type: "link",
 label: "Painel Analítico",
 path: isAdmin ? "/admin/analitico" : "/portal/analitico",
 icon: BarChart3,
 },
 ],
 [isAdmin],
 );

 const sectorEntries = useMemo(() => {
  if (user && ((user as any).hasSectorsAccess === false || (user as any).hasSectorsAccess === 0 || (user as any).hasSectorsAccess === '0')) {
  return [];
  }
 let list = sectorList;
 if (isManager && user?.managedSectors) {
 try {
 const managed = typeof user.managedSectors === 'string'
 ? JSON.parse(user.managedSectors)
 : (Array.isArray(user.managedSectors) ? user.managedSectors : []);
 list = sectorList.filter((s) => managed.includes(s.key));
 } catch {
 list = [];
 }
 }
 return list.map((meta) => ({ type: "sector" as const, meta }));
 }, [sectorList, isManager, user]);

  const adminLinks = useMemo(
    () => {
      if (!isAdmin) return [];
      const isLeader = (user as any)?.isGroupLeader === 1 || (user as any)?.isGroupLeader === true || (user as any)?.isGroupLeader === '1';
      const links = [
        ...(user?.role === "admin" ? [
          { label: "Planos & Franquias", path: "/admin/planos", icon: Crown },
          { label: "Configurações Globais", path: "/admin/configuracoes", icon: Settings }
        ] : (
          (user?.role === "admin-interno" && isLeader) ? [
            { label: "Meu Plano", path: "/admin/planos", icon: Crown }
          ] : []
        )),
        { label: "Empresas", path: "/admin/empresas", icon: Building2 },
        { label: "Usuários", path: "/admin/usuarios", icon: Users },
        { label: "Setores", path: "/admin/setores", icon: FolderOpen },
 { label: "Documentos", path: "/admin/documentos", icon: Files },
 { label: "Pendências", path: "/admin/pendencias", icon: CalendarClock },
 { label: "Vencimentos", path: "/admin/vencimentos", icon: Files },
 { label: "Blog", path: "/admin/blog", icon: BookOpen },
 { label: "Mensagens de contato", path: "/admin/contato", icon: MessageSquare },
 ];
 let filtered = links;
 if (user && ((user as any).hasSectorsAccess === false || (user as any).hasSectorsAccess === 0 || (user as any).hasSectorsAccess === '0')) {
 filtered = filtered.filter(
 (l) =>
 l.path !== "/admin/setores" &&
 l.path !== "/admin/documentos" &&
 l.path !== "/admin/pendencias" &&
 l.path !== "/admin/vencimentos"
 );
 }
 if (isManager) {
 return filtered.filter(
 (l) =>
 l.path !== "/admin/setores" &&
 l.path !== "/admin/blog" &&
 l.path !== "/admin/contato"
 );
 }
 if (user?.role === "admin-interno") {
 return filtered.filter(
 (l) =>
 l.path !== "/admin/blog" &&
 l.path !== "/admin/contato"
 );
 }
 return filtered;
 },
 [isAdmin, isManager, user],
 );

 const fiscalLinks = useMemo(() => {
    if (!isAdmin) {
      return [
        { label: "Clientes Fiscais", path: "/portal/clientes", icon: Users },
        { label: "Serviços", path: "/portal/servicos", icon: Briefcase },
      ];
    }
    const links = [
      { label: "Clientes Fiscais", path: "/admin/clientes", icon: Users },
      { label: "Serviços", path: "/admin/servicos", icon: Briefcase },
    ];
    if (user?.role === "admin" || user?.role === "admin-interno" || user?.role === "manager") {
      links.push({ label: "Logs de E-mail", path: "/admin/emails", icon: Mail });
    }
    if (user?.role === "admin") {
      links.push({ label: "Logs da Api/Motor", path: "/admin/focus/logs", icon: Activity });
    }
    return links;
  }, [isAdmin, user]);

 const headerTitle = useMemo(() => {
 if (location.startsWith("/admin/documentos")) {
 const params = new URLSearchParams(searchParamsStr);
 const secKey = params.get("sector");
 if (secKey) {
 const matchingSector = sectorList.find((s) => s.key === secKey);
 if (matchingSector) return `Auditando: ${matchingSector.label}`;
 }
 return "Auditando Documentos";
 }

 if (location.startsWith("/portal/notas/emitir") || location.startsWith("/admin/notas/emitir")) return "Emissão de Notas Fiscais";
 if (location.startsWith("/portal/guias") || location.startsWith("/admin/guias")) return "Central de Guias Fiscais (DCTFWeb & e-CAC)";
  if (location.startsWith("/portal/analitico") || location.startsWith("/admin/analitico")) return "Painel Analítico & BI Fiscal";
  if (location.startsWith("/admin/focus/logs")) return "Logs da Api/Motor";
 if (location.startsWith("/portal/notas") || location.startsWith("/admin/notas")) return "Todas as Notas Fiscais";
 if (location.startsWith("/admin/google-drive")) return "Integração Google Drive";

 if (location.startsWith("/admin")) return "Painel administrativo";
 
 const sector = sectorList.find((s) => location.startsWith(s.path || `/portal/${s.key}`));
 if (sector) return sector.label;
 if (location.startsWith("/portal/pendencias")) return "Minhas pendências";
 if (location.startsWith("/portal/vencimentos")) return "Próximos vencimentos";
 return "Área do Cliente";
 }, [location, sectorList, searchParamsStr]);

 const AlertBell = () => {
 const pendingsQuery = trpc.pendings.listMine.useQuery();
 const { isTokenInvalid } = useFocusMasterTokenLock();

 const openPendings = useMemo(() => {
 return (pendingsQuery.data || []).filter((p: any) => p.status === 'open' || p.status === 'in_progress');
 }, [pendingsQuery.data]);

 return (
 <div className="flex items-center gap-2">
 {isTokenInvalid && (
 <div 
   onClick={() => user?.role === "admin" && setLocation("/admin/configuracoes")}
   className={`flex items-center gap-1.5 text-amber-500 bg-amber-500/10 px-2.5 py-1.5 rounded-md border border-amber-500/20 shadow-sm ${user?.role === "admin" ? "cursor-pointer hover:bg-amber-500/20 transition-colors" : ""}`} 
   title="Token da Focus NFe inválido! Clique para configurar."
 >
 <AlertTriangle className="h-4.5 w-4.5" />
 <span className="text-xs font-medium hidden md:inline">Token NFe Inválido</span>
 </div>
 )}
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <button className="relative h-9 w-9 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring border-none bg-transparent cursor-pointer shrink-0">
 <Bell className="h-4.5 w-4.5 text-muted-foreground hover:text-foreground" />
 {openPendings.length > 0 && (
 <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-destructive flex items-center justify-center ring-2 ring-background">
 </span>
 )}
 </button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" className="w-80 p-0 rounded-none border shadow-lg bg-background">
 <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between">
 <h3 className="font-bold text-xs uppercase tracking-wider text-foreground">Alertas e Avisos</h3>
 {openPendings.length > 0 && (
 <span className="text-[10px] bg-destructive/10 text-destructive border border-destructive/20 font-bold px-1.5 py-0.5 rounded-none">
 {openPendings.length} pendentes
 </span>
 )}
 </div>
 <div className="max-h-64 overflow-y-auto divide-y divide-border">
 {openPendings.length > 0 ? (
 openPendings.map((p: any) => (
 <div
 key={p.id}
 onClick={() => setLocation(isAdmin ? "/admin/pendencias" : "/portal/pendencias")}
 className="p-3 hover:bg-muted/50 transition-colors cursor-pointer text-left"
 >
 <p className="text-xs font-bold text-foreground truncate">{p.title}</p>
 <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
 <p className="text-[9px] text-muted-foreground mt-1 font-semibold">
 Prazo: {new Date(p.dueDate).toLocaleDateString('pt-BR')}
 </p>
 </div>
 ))
 ) : (
 <div className="p-4 text-center text-xs text-muted-foreground">
 Nenhum alerta ou aviso pendente.
 </div>
 )}
 </div>
 <div className="p-2 border-t text-center">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setLocation(isAdmin ? "/admin/pendencias" : "/portal/pendencias")}
 className="w-full text-xs text-[var(--brand-orange)] font-bold rounded-none hover:bg-muted"
 >
 Ver todas as pendências
 </Button>
 </div>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>
 );
 };

 return (
 <>
 <Sidebar collapsible="icon" className="border-r">
 <SidebarHeader className="h-16 justify-center border-b">
 <div className="flex items-center gap-3 px-2 w-full">
 <button
 onClick={toggleSidebar}
 className="h-9 w-9 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
 aria-label="Alternar menu lateral"
 >
 <PanelLeft className="h-4 w-4 text-muted-foreground" />
 </button>
 {!isCollapsed ? (
 <SoFaciliteiLogo variant="horizontal" height={32} />
 ) : (
 <SoFaciliteiLogo variant="icon" height={32} />
 )}
 </div>
 </SidebarHeader>

 <SidebarContent className="gap-0">
 {/* Empresa do cliente */}
 {!isAdmin && !isCollapsed ? (
 <div className="px-4 pt-4 pb-2">
 <div className="rounded-xl border bg-card p-3">
 <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
 <Building2 className="h-3.5 w-3.5" />
 Empresa
 </div>
 <p className="mt-1 text-sm font-semibold leading-snug truncate">
 {myCompanyQuery.data?.tradeName || myCompanyQuery.data?.legalName || "Aguardando vínculo"}
 </p>
 {myCompanyQuery.data?.cnpj ? (
 <p className="text-xs text-muted-foreground mt-0.5">CNPJ {myCompanyQuery.data.cnpj}</p>
 ) : null}
 </div>
 </div>
 ) : null}

 <SidebarGroup>
 {!isCollapsed ? <SidebarGroupLabel>Visão geral</SidebarGroupLabel> : null}
 <SidebarMenu className="px-2">
 {overviewLinks.map((entry) => {
 const isActive =
 entry.path === "/portal" || entry.path === "/admin"
 ? location === entry.path
 : location === entry.path || location.startsWith(entry.path + "/");
 return (
 <SidebarMenuItem key={entry.path}>
 <SidebarMenuButton
 asChild
 isActive={isActive}
 tooltip={entry.label}
 className="h-10"
 >
 <Link href={entry.path}>
 <entry.icon className="h-4 w-4" />
 <span className="group-data-[collapsible=icon]:hidden">{entry.label}</span>
 </Link>
 </SidebarMenuButton>
 </SidebarMenuItem>
 );
 })}

  {!isAdmin && !(
  (myCompanyQuery.data && ((myCompanyQuery.data as any).hasSectorsAccess === false || (myCompanyQuery.data as any).hasSectorsAccess === 0 || (myCompanyQuery.data as any).hasSectorsAccess === '0')) ||
  (user && ((user as any).hasSectorsAccess === false || ((user as any).hasSectorsAccess === 0 || (user as any).hasSectorsAccess === '0')))
  ) ? (
 <>
 <SidebarMenuItem>
 <SidebarMenuButton
 asChild
 isActive={location === "/portal/pendencias"}
 tooltip="Pendências"
 className="h-10"
 >
 <Link href="/portal/pendencias">
 <CalendarClock className="h-4 w-4" />
 <span className="group-data-[collapsible=icon]:hidden">Pendências</span>
 </Link>
 </SidebarMenuButton>
 </SidebarMenuItem>
 <SidebarMenuItem>
 <SidebarMenuButton
 asChild
 isActive={location === "/portal/vencimentos"}
 tooltip="Próximos vencimentos"
 className="h-10"
 >
 <Link href="/portal/vencimentos">
 <Files className="h-4 w-4" />
 <span className="group-data-[collapsible=icon]:hidden">Vencimentos</span>
 </Link>
 </SidebarMenuButton>
 </SidebarMenuItem>
 </>
 ) : null}
 </SidebarMenu>
 </SidebarGroup>

 {/* NOTAS */}
 <SidebarGroup>
 <button
 onClick={() => setOpenNotas((v) => !v)}
 className="flex items-center justify-between px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors group-data-[collapsible=icon]:hidden bg-transparent border-none w-full text-left cursor-pointer"
 >
 <span>Notas Fiscais</span>
 <ChevronDown
 className={`h-3.5 w-3.5 transition-transform ${openNotas ? "" : "-rotate-90"}`}
 />
 </button>
 {openNotas ? (
 <SidebarMenu className="px-2">
 <SidebarMenuItem>
 <SidebarMenuButton
 asChild
 isActive={location === (isAdmin ? "/admin/notas/emitir" : "/portal/notas/emitir")}
 tooltip="Emissão de Notas"
 className={`h-10 transition-all duration-200 ${
 location === (isAdmin ? "/admin/notas/emitir" : "/portal/notas/emitir")
 ? "bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] font-semibold border-l-2 bg-[var(--brand-purple)]"
 : "hover:bg-muted/50"
 }`}
 >
 <Link href={isAdmin ? "/admin/notas/emitir" : "/portal/notas/emitir"}>
 <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--brand-purple)] shrink-0" aria-hidden />
 <span className="truncate group-data-[collapsible=icon]:hidden">Emissão de Notas</span>
 </Link>
 </SidebarMenuButton>
 </SidebarMenuItem>
 <SidebarMenuItem>
 <SidebarMenuButton
 asChild
 isActive={location === (isAdmin ? "/admin/notas" : "/portal/notas")}
 tooltip="Todas as notas"
 className={`h-10 transition-all duration-200 ${
 location === (isAdmin ? "/admin/notas" : "/portal/notas")
 ? "bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] font-semibold border-l-2 bg-[var(--brand-purple)]"
 : "hover:bg-muted/50"
 }`}
 >
 <Link href={isAdmin ? "/admin/notas" : "/portal/notas"}>
 <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--brand-purple)] shrink-0" aria-hidden />
 <span className="truncate group-data-[collapsible=icon]:hidden">Todas as notas</span>
 </Link>
 </SidebarMenuButton>
 </SidebarMenuItem>
 <SidebarMenuItem>
 <SidebarMenuButton
 asChild
 isActive={location === (isAdmin ? "/admin/guias" : "/portal/guias")}
 tooltip="Central de Guias (DCTFWeb & e-CAC)"
 className={`h-10 transition-all duration-200 ${
 location === (isAdmin ? "/admin/guias" : "/portal/guias")
 ? "bg-blue-600/15 text-blue-600 font-semibold border-l-2 bg-blue-600"
 : "hover:bg-muted/50"
 }`}
 >
 <Link href={isAdmin ? "/admin/guias" : "/portal/guias"}>
 <Landmark className="h-4 w-4 text-blue-600 shrink-0" aria-hidden />
 <span className="truncate group-data-[collapsible=icon]:hidden">Central de Guias (DCTFWeb)</span>
 </Link>
 </SidebarMenuButton>
 </SidebarMenuItem>

 {fiscalLinks.length > 0 && (
 <SidebarMenuItem className="mt-1">
 <button
 onClick={(e) => {
 e.stopPropagation();
 setOpenFiscal((v) => !v);
 }}
 className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer focus:outline-none"
 >
 <span className="flex items-center gap-2">
 <Receipt className="h-3.5 w-3.5 text-[var(--brand-orange)]" />
 <span className="text-[var(--brand-orange)] font-bold">Módulo Fiscal</span>
 </span>
 <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openFiscal ? "" : "-rotate-90"}`} />
 </button>
 {openFiscal && (
 <SidebarMenuSub className="pl-2 border-l-2 border-[var(--brand-orange)]/30 ml-4 mt-1 space-y-1">
 {fiscalLinks.map((link) => {
 const isActive = location.startsWith(link.path);
 return (
 <SidebarMenuSubItem key={link.path}>
 <SidebarMenuSubButton
 asChild
 isActive={isActive}
 className={`h-10 transition-all duration-200 ${
 isActive
 ? "bg-[var(--brand-orange)]/15 text-[var(--brand-orange)] font-semibold border-l-2 border-[var(--brand-orange)]"
 : "hover:bg-muted/50"
 }`}
 >
 <Link href={link.path}>
 <link.icon className="h-4 w-4" />
 <span className="truncate">{link.label}</span>
 </Link>
 </SidebarMenuSubButton>
 </SidebarMenuSubItem>
 );
 })}
 </SidebarMenuSub>
 )}
 </SidebarMenuItem>
 )}
 </SidebarMenu>
 ) : null}
 </SidebarGroup>

 {/* Setores */}
  {(
  (user && ((user as any).hasSectorsAccess === false || (user as any).hasSectorsAccess === 0 || (user as any).hasSectorsAccess === '0')) ||
  (!isAdmin && myCompanyQuery.data && ((myCompanyQuery.data as any).hasSectorsAccess === false || (myCompanyQuery.data as any).hasSectorsAccess === 0 || (myCompanyQuery.data as any).hasSectorsAccess === '0'))
  ) ? null : (
 <SidebarGroup>
 <button
 onClick={() => setOpenSetores((v) => !v)}
 className="flex items-center justify-between px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors group-data-[collapsible=icon]:hidden bg-transparent border-none w-full text-left cursor-pointer"
 >
 <span>Setores</span>
 <ChevronDown
 className={`h-3.5 w-3.5 transition-transform ${openSetores ? "" : "-rotate-90"}`}
 />
 </button>
 {openSetores ? (
 <SidebarMenu className="px-2">
 {sectorEntries.map(({ meta }) => {
 const tone = sectorToneClasses(meta.tone as any || "orange");
 
 const targetPath = isAdmin
 ? `/admin/documentos`
 : meta.path || `/portal/${meta.key}`;
 
 const queryParams = isAdmin ? `?sector=${meta.key}` : "";
 
 const currentParams = new URLSearchParams(searchParamsStr);
 const currentSector = currentParams.get("sector");
 const currentCategory = currentParams.get("category");

 const isActive = isAdmin
 ? location.startsWith("/admin/documentos") && currentSector === meta.key
 : location.startsWith(meta.path || `/portal/${meta.key}`);

 let categoriesList: string[] = [];
 try {
 categoriesList = JSON.parse(meta.categories || "[]");
 } catch {
 categoriesList = [];
 }

 return (
 <SidebarMenuItem key={meta.key}>
 <SidebarMenuButton
 asChild
 isActive={isActive}
 tooltip={meta.label}
 className={`h-10 transition-all duration-200 ${
 isActive
 ? `${tone.chip} font-semibold border-l-2 ${tone.bar}`
 : "hover:bg-muted/50"
 }`}
 >
 <Link href={`${targetPath}${queryParams}`}>
 <span
 className={`inline-block h-2.5 w-2.5 rounded-full ${tone.bar} shrink-0`}
 aria-hidden
 />
 <span className="truncate group-data-[collapsible=icon]:hidden">{meta.label}</span>
 </Link>
 </SidebarMenuButton>

 {/* Submenu de Categorias */}
 {isActive && !isCollapsed && (
 <SidebarMenuSub className="mt-1 ml-5 border-l pl-2 space-y-1">
 <SidebarMenuSubItem>
 <SidebarMenuSubButton
 asChild
 isActive={!currentCategory || currentCategory === "all" || currentCategory === "todas"}
 className={`text-[11px] h-8 transition-all duration-200 cursor-pointer ${
 (!currentCategory || currentCategory === "all" || currentCategory === "todas")
 ? `${tone.text} font-bold bg-muted/40`
 : "text-muted-foreground hover:text-foreground"
 }`}
 >
 <Link href={`${targetPath}${queryParams}`}>
 <span>Todas</span>
 </Link>
 </SidebarMenuSubButton>
 </SidebarMenuSubItem>
 {categoriesList.map((cat) => {
 const isCatActive = currentCategory === cat;
 const catQuery = isAdmin
 ? `?sector=${meta.key}&category=${encodeURIComponent(cat)}`
 : `?category=${encodeURIComponent(cat)}`;
 return (
 <SidebarMenuSubItem key={cat}>
 <SidebarMenuSubButton
 asChild
 isActive={isCatActive}
 className={`text-[11px] h-8 transition-all duration-200 cursor-pointer ${
 isCatActive
 ? `${tone.text} font-bold bg-muted/40`
 : "text-muted-foreground hover:text-foreground"
 }`}
 >
 <Link href={`${targetPath}${catQuery}`}>
 <span>{cat}</span>
 </Link>
 </SidebarMenuSubButton>
 </SidebarMenuSubItem>
 );
 })}
 </SidebarMenuSub>
 )}
 </SidebarMenuItem>
 );
 })}
 </SidebarMenu>
 ) : null}
 </SidebarGroup>
 )}

  {isAdmin ? (
    <SidebarGroup>
      <button
        onClick={() => setOpenAdmin((v) => !v)}
        className="flex items-center justify-between px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors group-data-[collapsible=icon]:hidden bg-transparent border-none w-full text-left cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" />
          Administração
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${openAdmin ? "" : "-rotate-90"}`}
        />
      </button>
      {openAdmin ? (
        <SidebarMenu className="px-2">
          {adminLinks.map((link) => {
            const isActive = location === link.path;
            return (
              <SidebarMenuItem key={link.path}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  tooltip={link.label}
                  className={`h-10 transition-all duration-200 ${
                    isActive
                      ? "bg-[var(--brand-orange)]/15 text-[var(--brand-orange)] font-semibold border-l-2 border-[var(--brand-orange)]"
                      : "hover:bg-muted/50"
                  }`}
                >
                  <Link href={link.path}>
                    <link.icon className="h-4 w-4" />
                    <span className="group-data-[collapsible=icon]:hidden">{link.label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      ) : null}
    </SidebarGroup>
  ) : null}
</SidebarContent>

  <SidebarFooter className="p-3 border-t">
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring border-none bg-transparent cursor-pointer">
          <Avatar className="h-9 w-9 border shrink-0">
            <AvatarFallback className="text-xs font-medium bg-[var(--brand-orange)]/15 text-[var(--brand-orange)]">
              {user?.name?.charAt(0).toUpperCase() ?? "C"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
            <p className="text-sm font-medium truncate leading-none">{user?.name || "-"}</p>
            <p className="text-xs text-muted-foreground truncate mt-1.5">{user?.email || "-"}</p>
            <p className="text-[10px] text-muted-foreground/60 truncate mt-1">v3.1.133</p>
          </div>
          {!isCollapsed && isImpersonating ? (
            <Badge className="bg-amber-500/20 text-amber-800 dark:text-amber-300 border-amber-500/30 text-[9px] uppercase font-bold">
              Simulação
            </Badge>
          ) : !isCollapsed && isAdmin ? (
            <Badge variant="secondary" className="text-[10px] uppercase">
              Equipe
            </Badge>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Minha conta</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isImpersonating && (
          <>
            <DropdownMenuItem
              onClick={() => unimpersonateMut.mutate()}
              disabled={unimpersonateMut.isPending}
              className="cursor-pointer bg-amber-500/15 text-amber-900 dark:text-amber-200 hover:bg-amber-500/25 focus:bg-amber-500/25 font-bold text-xs flex items-center gap-2 py-2"
            >
              <LogOut className="mr-2 h-4 w-4 text-amber-600" />
              {unimpersonateMut.isPending ? "Retornando..." : "Voltar ao Painel Admin"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {isAdmin ? (
          <>
            <DropdownMenuItem onClick={() => setLocation("/admin")} className="cursor-pointer">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              Painel administrativo
            </DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem onClick={() => setLocation("/admin/configuracoes")} className="cursor-pointer font-medium text-foreground">
                <Settings className="mr-2 h-4 w-4 text-purple-600" />
                Configurações Globais (Token NFe)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => toggleTheme(user?.id)} className="cursor-pointer font-medium text-foreground">
              {theme === "dark" ? (
                <>
                  <Sun className="mr-2 h-4 w-4 text-amber-500" />
                  Modo Claro
                </>
              ) : (
                <>
                  <Moon className="mr-2 h-4 w-4 text-purple-500" />
                  Modo Escuro (Obsidian)
                </>
              )}
            </DropdownMenuItem>
            {isSystemAdmin && (
              <DropdownMenuItem onClick={() => setOpenThemeModal(true)} className="cursor-pointer font-medium text-foreground">
                <Palette className="mr-2 h-4 w-4 text-[var(--brand-orange)]" />
                Aparência & Cores
              </DropdownMenuItem>
            )}
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={() => setLocation("/portal")} className="cursor-pointer">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              Início do portal
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => toggleTheme(user?.id)} className="cursor-pointer font-medium text-foreground">
              {theme === "dark" ? (
                <>
                  <Sun className="mr-2 h-4 w-4 text-amber-500" />
                  Modo Claro
                </>
              ) : (
                <>
                  <Moon className="mr-2 h-4 w-4 text-purple-500" />
                  Modo Escuro (Obsidian)
                </>
              )}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={logout}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sair
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1 text-[9px] text-muted-foreground/50 text-center select-none">
          Versão v3.1.133 (Produção)
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  </SidebarFooter>
</Sidebar>

<SidebarInset className="bg-background transition-colors duration-200">
  {isMobile ? (
    <div className="flex border-b h-14 items-center justify-between bg-background/95 px-3 backdrop-blur sticky top-0 z-40 gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <SidebarTrigger className="h-9 w-9 rounded-lg bg-background shrink-0" />
        <span className="font-display font-bold text-base truncate">{headerTitle}</span>
      </div>
      <div className="flex items-center gap-2">
        {isImpersonating && (
          <button
            onClick={() => unimpersonateMut.mutate()}
            disabled={unimpersonateMut.isPending}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 dark:text-amber-300 border border-amber-500/30 text-[11px] font-bold cursor-pointer transition-all shrink-0"
            title="Encerrar simulação e voltar ao Painel Admin"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
            </span>
            <LogOut className="h-3 w-3" />
            <span>Voltar Admin</span>
          </button>
        )}
        <button
          onClick={() => toggleTheme(user?.id)}
          className="h-9 w-9 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring border-none bg-transparent cursor-pointer shrink-0 text-muted-foreground hover:text-foreground"
          title={theme === "dark" ? "Alternar para Modo Claro" : "Alternar para Modo Escuro"}
          aria-label="Alternar tema"
        >
          {theme === "dark" ? <Sun className="h-4.5 w-4.5 text-amber-400" /> : <Moon className="h-4.5 w-4.5" />}
        </button>
        <AlertBell />
        <SoFaciliteiLogo variant="horizontal" height={28} className="shrink-0" />
      </div>
    </div>
  ) : (
    <div className="flex border-b h-14 lg:h-16 items-center justify-between px-4 sm:px-6 bg-background/80 backdrop-blur sticky top-0 z-30">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <SidebarTrigger className="h-8 w-8 -ml-1.5 text-muted-foreground hover:text-foreground shrink-0" title="Recolher/Expandir Barra Lateral" />
        <h1 className="font-display font-bold text-lg sm:text-xl tracking-tight truncate">{headerTitle}</h1>
        {user?.role === "admin" && (
          <Badge className="bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] border-[var(--brand-purple)]/20">
            Admin Mestre
          </Badge>
        )}
        {user?.role === "admin-interno" && (
          <Badge className="bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] border-[var(--brand-purple)]/20">
            Admin Interno
          </Badge>
        )}
        {user?.role === "manager" && (
          <Badge className="bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] border-[var(--brand-purple)]/20">
            Gerente
          </Badge>
        )}
        {user?.role === "user" && (
          <Badge className="bg-[var(--brand-orange)]/15 text-[var(--brand-orange)] border-[var(--brand-orange)]/20">
            Cliente
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <button
          onClick={() => toggleTheme(user?.id)}
          className="h-9 w-9 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring border-none bg-transparent cursor-pointer shrink-0 text-muted-foreground hover:text-foreground"
          title={theme === "dark" ? "Alternar para Modo Claro" : "Alternar para Modo Escuro (Obsidian)"}
          aria-label="Alternar tema"
        >
          {theme === "dark" ? (
            <Sun className="h-4.5 w-4.5 text-amber-400 animate-in fade-in zoom-in duration-200" />
          ) : (
            <Moon className="h-4.5 w-4.5 animate-in fade-in zoom-in duration-200" />
          )}
        </button>
        <AlertBell />
        {isImpersonating ? (
          <div className="border-l pl-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 dark:text-amber-300 border border-amber-500/30 text-xs font-semibold transition-all shadow-2xs cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  title="Modo de Simulação ativo. Clique para opções."
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <span className="hidden lg:inline text-amber-700 dark:text-amber-400 font-medium">Simulação:</span>
                  <span className="font-bold">Olá, {user?.name?.split(" ")[0] ?? "Cliente"}</span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 p-3 shadow-xl border-amber-200/60 dark:border-amber-900/60">
                <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-amber-200/70 dark:border-amber-800/40 mb-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900 dark:text-amber-200">
                    <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
                    Modo de Simulação Ativo
                  </div>
                  <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90 mt-1 leading-snug">
                    Você está operando a conta de <strong>{user?.name}</strong> ({user?.email})
                  </p>
                </div>
                <DropdownMenuItem
                  onClick={() => unimpersonateMut.mutate()}
                  disabled={unimpersonateMut.isPending}
                  className="cursor-pointer bg-amber-600 hover:bg-amber-700 text-white focus:bg-amber-700 focus:text-white font-semibold text-xs py-2.5 justify-center rounded-lg flex items-center gap-2 shadow-xs transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  {unimpersonateMut.isPending ? "Retornando ao Admin..." : "Voltar ao Painel Admin"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 border-l pl-4 h-5">
            <Users className="h-4 w-4" />
            <span className="hidden md:inline">Olá, {user?.name?.split(" ")[0] ?? "cliente"}</span>
          </div>
        )}
      </div>
    </div>
  )}
  <main className="flex-1 p-3 sm:p-5 lg:p-6 min-w-0 overflow-x-hidden">{children}</main>
</SidebarInset>
 <AdminThemeModal open={openThemeModal} onOpenChange={setOpenThemeModal} />
 </>
 );
}
